export interface IconShape {
  tag: 'path' | 'circle' | 'rect' | 'line' | 'polyline' | 'ellipse';
  d?: string;
  cx?: string;
  cy?: string;
  r?: string;
  rx?: string;
  ry?: string;
  x?: string;
  y?: string;
  width?: string;
  height?: string;
  x1?: string;
  y1?: string;
  x2?: string;
  y2?: string;
  points?: string;
  /** Overrides the svg-level 'none'/'currentColor' defaults for this shape only. */
  fill?: string;
  stroke?: string;
  opacity?: string;
}

/**
 * Custom line-icon set for the home page, drawn on a 24x24 grid with a
 * consistent 1.75 stroke weight. Keys match the Material Icons ligature
 * names they replace, so callers don't need to change.
 *
 * Rendered as real Angular-created SVG child elements (not innerHTML) so it
 * works under SSR/prerendering, where SVGSVGElement.innerHTML isn't implemented.
 */
export const HOME_ICON_SHAPES: Record<string, IconShape[]> = {
  home: [
    { tag: 'path', d: 'M4 11.5 12 4l8 7.5' },
    { tag: 'path', d: 'M6 10v9a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-9' },
    { tag: 'path', d: 'M10 20v-5h4v5' },
  ],
  payments: [
    { tag: 'rect', x: '3', y: '7', width: '14', height: '10', rx: '2' },
    { tag: 'rect', x: '7', y: '4', width: '14', height: '10', rx: '2', opacity: '.55' },
  ],
  credit_card: [
    { tag: 'rect', x: '3', y: '6', width: '18', height: '12', rx: '2' },
    { tag: 'line', x1: '3', y1: '10', x2: '21', y2: '10' },
    { tag: 'line', x1: '6', y1: '15', x2: '10', y2: '15' },
  ],
  directions_car: [
    { tag: 'rect', x: '4', y: '12', width: '16', height: '4', rx: '1.5' },
    { tag: 'path', d: 'M6 12l1.5-4h9l1.5 4' },
    { tag: 'circle', cx: '8', cy: '17.5', r: '1.5' },
    { tag: 'circle', cx: '16', cy: '17.5', r: '1.5' },
  ],
  account_balance: [
    { tag: 'path', d: 'M12 3l9 5H3z' },
    { tag: 'path', d: 'M5 10v7M9.5 10v7M14.5 10v7M19 10v7' },
    { tag: 'path', d: 'M4 19h16' },
  ],
  child_care: [
    { tag: 'circle', cx: '8', cy: '7', r: '2.2' },
    { tag: 'path', d: 'M4.5 19v-2.2a3.5 3.5 0 0 1 7 0V19' },
    { tag: 'circle', cx: '16.5', cy: '10.5', r: '1.6' },
    { tag: 'path', d: 'M14 19v-1.6a2.5 2.5 0 0 1 5 0V19' },
  ],
  savings: [
    { tag: 'ellipse', cx: '11', cy: '13', rx: '7', ry: '5' },
    { tag: 'path', d: 'M18 12l2.5-1.5v4L18 13' },
    { tag: 'circle', cx: '15.5', cy: '11.5', r: '.7', fill: 'currentColor', stroke: 'none' },
    { tag: 'path', d: 'M9 9V7M7 17v2M15 17v2' },
    { tag: 'path', d: 'M10.5 10.5h3' },
  ],
  autorenew: [
    { tag: 'path', d: 'M4 12a8 8 0 0 1 13.5-5.8M20 12a8 8 0 0 1-13.5 5.8' },
    { tag: 'path', d: 'M17 3v4h-4M7 21v-4h4' },
  ],
  percent: [
    { tag: 'line', x1: '5', y1: '19', x2: '19', y2: '5' },
    { tag: 'circle', cx: '7.5', cy: '7.5', r: '2.2' },
    { tag: 'circle', cx: '16.5', cy: '16.5', r: '2.2' },
  ],
  price_change: [
    {
      tag: 'path',
      d: 'M11 4H5a1 1 0 0 0-1 1v6a1 1 0 0 0 .3.7l8 8a1 1 0 0 0 1.4 0l6-6a1 1 0 0 0 0-1.4l-8-8A1 1 0 0 0 11 4z',
    },
    { tag: 'circle', cx: '8', cy: '8', r: '1.6', fill: 'currentColor', stroke: 'none' },
  ],
  apartment: [
    { tag: 'rect', x: '5', y: '3', width: '14', height: '18', rx: '1' },
    { tag: 'path', d: 'M8 7h2M14 7h2M8 11h2M14 11h2M8 15h2M14 15h2' },
    { tag: 'path', d: 'M10 21v-4h4v4' },
  ],
  elderly: [
    { tag: 'path', d: 'M12 3 19 6.5V12c0 4.5-3 8-7 9-4-1-7-4.5-7-9V6.5z' },
    { tag: 'circle', cx: '12', cy: '11', r: '3' },
    { tag: 'path', d: 'M12 9.5V11l1.3.9' },
  ],
  monetization_on: [
    { tag: 'circle', cx: '12', cy: '12', r: '8.5' },
    { tag: 'path', d: 'M9 8.5h5M9 12h3.5M9 8.5v7l4.5-3.2' },
  ],
  trending_up: [
    { tag: 'polyline', points: '4,17 10,11 14,15 20,7' },
    { tag: 'polyline', points: '14,7 20,7 20,13' },
  ],
  stairs: [
    { tag: 'polyline', points: '4,20 4,16 9,16 9,12 14,12 14,8 19,8 19,4' },
    { tag: 'path', d: 'M4 20h15', opacity: '.55' },
  ],
  compare_arrows: [
    { tag: 'path', d: 'M7 6h13M17 2.5 20.5 6 17 9.5' },
    { tag: 'path', d: 'M17 18H4M7 14.5 3.5 18 7 21.5' },
  ],
  swap_horiz: [
    { tag: 'path', d: 'M4 9h13M14 5l3 4-3 4' },
    { tag: 'path', d: 'M20 15H7M10 19l-3-4 3-4' },
  ],
  call_made: [
    { tag: 'line', x1: '7', y1: '17', x2: '17', y2: '7' },
    { tag: 'polyline', points: '9,7 17,7 17,15' },
  ],
  local_fire_department: [
    {
      tag: 'path',
      d: 'M12 3c1 3-3 4-3 7.5a3 3 0 0 0 6 0c0-1-.5-1.8-1-2.5.8.3 2 1.6 2 4a4 4 0 0 1-8 0c0-4 2.5-5.5 4-9z',
    },
  ],
  security: [
    { tag: 'path', d: 'M12 3 19 6.5V12c0 4.5-3 8-7 9-4-1-7-4.5-7-9V6.5z' },
    { tag: 'polyline', points: '9,12 11,14 15,9.5' },
  ],
  receipt_long: [
    { tag: 'path', d: 'M6 3h12v17l-2-1.3L14 20l-2-1.3L10 20l-2-1.3L6 20z' },
    { tag: 'path', d: 'M9 7h6M9 10.5h6M9 14h4' },
  ],
  location_city: [
    { tag: 'rect', x: '3', y: '9', width: '7', height: '12' },
    { tag: 'rect', x: '12', y: '4', width: '9', height: '17' },
    { tag: 'path', d: 'M5.5 12h2M5.5 15h2M5.5 18h2M14.5 7h2M14.5 10h2M14.5 13h2M14.5 16h2' },
  ],
  report_problem: [
    { tag: 'path', d: 'M12 3.5 21.5 20h-19z' },
    { tag: 'line', x1: '12', y1: '9.5', x2: '12', y2: '14' },
    { tag: 'circle', cx: '12', cy: '17', r: '.9', fill: 'currentColor', stroke: 'none' },
  ],
  notifications: [
    { tag: 'path', d: 'M6 16v-4.5a6 6 0 0 1 12 0V16l1.5 2h-15z' },
    { tag: 'path', d: 'M10.5 20a1.5 1.5 0 0 0 3 0' },
  ],
  search: [
    { tag: 'circle', cx: '10.5', cy: '10.5', r: '6.5' },
    { tag: 'line', x1: '15.3', y1: '15.3', x2: '20.5', y2: '20.5' },
  ],
  account_balance_wallet: [
    {
      tag: 'path',
      d: 'M4 8a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v1h1a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H6a2 2 0 0 1-2-2z',
    },
    { tag: 'circle', cx: '16.5', cy: '13', r: '1.3', fill: 'currentColor', stroke: 'none' },
  ],
  home_work: [
    { tag: 'path', d: 'M4 11.5 12 4l8 7.5' },
    { tag: 'path', d: 'M6 10v9a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-9' },
    { tag: 'circle', cx: '9.5', cy: '16', r: '1.6' },
    { tag: 'path', d: 'M11 16h4.5M13.5 16v1.5M15.5 16v1' },
  ],
  description: [
    { tag: 'path', d: 'M7 3h7l4 4v14H7z' },
    { tag: 'path', d: 'M14 3v4h4' },
    { tag: 'path', d: 'M9.5 12h6M9.5 15h6M9.5 9h3' },
  ],
  markdown: [
    { tag: 'rect', x: '3', y: '5', width: '18', height: '14', rx: '2' },
    { tag: 'path', d: 'M6 15V9l3 3.5L12 9v6' },
    { tag: 'path', d: 'M15 9v6M15 15l2.5-2.5M15 15l-2.5-2.5' },
  ],
  menu_book: [
    { tag: 'path', d: 'M12 6c-1.8-1.3-4.3-2-7-2v13c2.7 0 5.2.7 7 2 1.8-1.3 4.3-2 7-2V4c-2.7 0-5.2.7-7 2z' },
    { tag: 'path', d: 'M12 6v13' },
  ],
  schedule: [
    { tag: 'circle', cx: '12', cy: '12', r: '8.5' },
    { tag: 'path', d: 'M12 7.5V12l3.5 2' },
  ],
  lock: [
    { tag: 'rect', x: '6', y: '11', width: '12', height: '9', rx: '1.5' },
    { tag: 'path', d: 'M8 11V8a4 4 0 0 1 8 0v3' },
    { tag: 'circle', cx: '12', cy: '15', r: '.9', fill: 'currentColor', stroke: 'none' },
  ],
  person: [
    { tag: 'circle', cx: '12', cy: '8', r: '3.2' },
    { tag: 'path', d: 'M5.5 20v-1.5a6.5 6.5 0 0 1 13 0V20' },
  ],
  sitemap: [
    { tag: 'circle', cx: '12', cy: '5', r: '2' },
    { tag: 'path', d: 'M12 7v3M12 10 6 14M12 10l6 4' },
    { tag: 'circle', cx: '6', cy: '16', r: '2' },
    { tag: 'circle', cx: '18', cy: '16', r: '2' },
  ],
  bolt: [
    { tag: 'path', d: 'M13 2 5 14h5l-1 8 9-12h-5z' },
  ],
  map: [
    { tag: 'path', d: 'M9 4 4 6v14l5-2 6 2 5-2V4l-5 2-6-2z' },
    { tag: 'line', x1: '9', y1: '4', x2: '9', y2: '18' },
    { tag: 'line', x1: '15', y1: '6', x2: '15', y2: '20' },
  ],
  assignment: [
    { tag: 'rect', x: '6', y: '4', width: '12', height: '17', rx: '2' },
    { tag: 'rect', x: '9', y: '2.5', width: '6', height: '3', rx: '1' },
    { tag: 'line', x1: '9', y1: '11', x2: '15', y2: '11' },
    { tag: 'line', x1: '9', y1: '14.5', x2: '15', y2: '14.5' },
    { tag: 'line', x1: '9', y1: '18', x2: '13', y2: '18' },
  ],
  location_on: [
    { tag: 'path', d: 'M12 3a6.5 6.5 0 0 0-6.5 6.5c0 5 6.5 11.5 6.5 11.5s6.5-6.5 6.5-11.5A6.5 6.5 0 0 0 12 3z' },
    { tag: 'circle', cx: '12', cy: '9.5', r: '2.2' },
  ],
};
